import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import {
  hostDisplayContents,
  requestInformationStyles
} from 'components/ordering/quotation/components/quotation.style.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import {
  activeQuotationContext,
  localeContext,
  NotFetchedYet
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { consume } from '@lit/context'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'

@customElement('csc-quotation-request-information')
export class CSCQuotationRequestInformation extends LitElement {
  static override styles: CSSResultGroup = [hostDisplayContents, requestInformationStyles]

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  private get totalColumns() {
    let totalColumns = 0

    if (this.reference) totalColumns += 1
    if (this.validityDate) totalColumns += 1
    if (this.name) totalColumns += 1
    if (this.note) totalColumns += 1
    if (this.reasonOfDecline) totalColumns += 1

    return totalColumns
  }

  get reference(): string | null {
    if (!isQuotationDTO(this.quotation)) return null
    return this.quotation.externalReference || null
  }

  @consume({ context: localeContext })
  private locale!: Intl.LocalesArgument

  get validityDate() {
    if (!isQuotationDTO(this.quotation) || !this.quotation.validityDate) return null

    return  new Intl.DateTimeFormat(this.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(this.quotation.validityDate))
  }

  get name(): string | null {
    if (!isQuotationDTO(this.quotation)) return null

    return this.quotation.name
  }

  get note(): string | null {
    if (!isQuotationDTO(this.quotation)) return null

    return this.quotation.note
  }

  get reasonOfDecline(): string | null {
    if (!isQuotationDTO(this.quotation)) return null

    if (['declined', 'declined_by_merchant'].includes(this.quotation.status)) {
      return this.quotation.reasonOfDecline || null
    }
    return null
  }

  @state()
  private get isVisible(): boolean {
    return isQuotationDTO(this.quotation) && this.quotation.status !== 'open' && this.totalColumns > 0
  }

  renderReference() {
    if (!this.reference) {
      return nothing
    }

    return html`
      <cse-text-group text-alignment="left">
        <cse-text slot="heading" variant="heading-5">
          ${t('csc_quotation_request_information_reference', {
            ns: 'csc-quotation-request-information',
            default: 'ERP Reference'
          })}
        </cse-text>
        <cse-text variant="body">${this.reference}</cse-text>
      </cse-text-group>
    `
  }

  renderValidityDate() {
    if (!this.validityDate) {
      return nothing
    }

    return html`
      <cse-text-group text-alignment="left">
        <cse-text slot="heading" variant="heading-5">
          ${t('csc_quotation_request_information_validity_date', {
            ns: 'csc-quotation-request-information',
            default: 'Valid until'
          })}
        </cse-text>
        <cse-text variant="body">${this.validityDate}</cse-text>
      </cse-text-group>
    `
  }

  renderName() {
    if (!this.name) {
      return nothing
    }

    return html`
      <cse-text-group text-alignment="left">
        <cse-text slot="heading" variant="heading-5">
          ${t('csc_quotation_request_information_name', {
            ns: 'csc-quotation-request-information',
            default: 'Reference'
          })}
        </cse-text>
        <cse-text variant="body">${this.name}</cse-text>
      </cse-text-group>
    `
  }


  renderNote() {
    if (!this.note) {
      return nothing
    }

    return html`
      <cse-text-group text-alignment="left">
        <cse-text slot="heading" variant="heading-5">
          ${t('csc_quotation_request_information_note', {
            ns: 'csc-quotation-request-information',
            default: 'Comment'
          })}
        </cse-text>
        <cse-text variant="body">${this.note}</cse-text>
      </cse-text-group>
    `
  }

  renderReasonOfDecline() {
    if (!isQuotationDTO(this.quotation) || !this.reasonOfDecline) {
      return nothing
    }

    return html`
      <cse-text-group text-alignment="left">
        <cse-text slot="heading" variant="heading-5">
          ${t('csc_quotation_request_information_reason_of_decline', {
            ns: 'csc-quotation-request-information',
            default: 'Reason of decline'
          })}
        </cse-text>
        <cse-text variant="body">${this.quotation.reasonOfDecline}</cse-text>
      </cse-text-group>
    `
  }

  override render() {
    if (!this.isVisible) {
      return nothing
    }

    return html`
      <cse-paper>
        <div class="columns-${this.totalColumns}">
          ${this.renderReference()}
          ${this.renderValidityDate()}
          ${this.renderName()}
          ${this.renderNote()}
          ${this.renderReasonOfDecline()}
        </div>
      </cse-paper>
    `
  }
}
